import Button from '@mui/joy/Button'
import axios from 'axios'
import Card from '@mui/joy/Card'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { datTokenProtectionApi, type DatTokenOwner } from './api'

function getErrorMessage(reason: unknown, fallback: string): string {
    if (axios.isAxiosError<{ message?: unknown }>(reason) && typeof reason.response?.data?.message === 'string') return reason.response.data.message
    return reason instanceof Error ? reason.message : fallback
}

export default function DatTokenProtection() {
    const [owners, setOwners] = React.useState<DatTokenOwner[]>([])
    const [blocked, setBlocked] = React.useState<string[]>([])
    const [projectName, setProjectName] = React.useState('')
    const [error, setError] = React.useState<string | null>(null)
    const [loading, setLoading] = React.useState(true)

    const load = React.useCallback(async () => {
        setLoading(true)
        try {
            const data = await datTokenProtectionApi.get()
            setOwners(data.owners)
            setBlocked(data.blockedProjectNames)
            setError(null)
        } catch (reason) {
            setError(getErrorMessage(reason, 'Failed to load DAT token protection'))
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => {
        void load()
    }, [load])

    const update = async (action: 'block' | 'unblock', value: string) => {
        try {
            const result = action === 'block' ? await datTokenProtectionApi.block(value) : await datTokenProtectionApi.unblock(value)
            setBlocked(result.blockedProjectNames)
            setProjectName('')
            setError(null)
        } catch (reason) {
            setError(getErrorMessage(reason, 'Failed to update DAT token protection'))
            void load()
        }
    }

    return (
        <Sheet sx={{ p: 3, minHeight: '100%', backgroundColor: 'background.body' }}>
            <Typography level="h2">DAT Token Protection</Typography>
            <Typography level="body-sm" sx={{ mb: 2 }}>
                Block a project from contributing its DAT token to shared random searches. Tokens are never displayed here.
            </Typography>
            <Card sx={{ mb: 2, gap: 1 }}>
                <Typography level="title-sm">Protect a project</Typography>
                <form
                    onSubmit={(event) => {
                        event.preventDefault()
                        if (projectName.trim()) void update('block', projectName.trim())
                    }}
                >
                    <Input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Exact project name" />
                    <Button type="submit" sx={{ mt: 1 }} disabled={!projectName.trim()}>
                        Block from shared pool
                    </Button>
                </form>
            </Card>
            {error && (
                <Typography color="danger" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            <Card sx={{ mb: 2 }}>
                <Typography level="title-sm">Blocked projects ({blocked.length})</Typography>
                {blocked.length === 0 ? (
                    <Typography level="body-sm">No projects are protected.</Typography>
                ) : (
                    blocked.map((name) => (
                        <Sheet key={name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography>{name}</Typography>
                            <Button size="sm" variant="outlined" color="danger" onClick={() => void update('unblock', name)}>
                                Remove
                            </Button>
                        </Sheet>
                    ))
                )}
            </Card>
            <Card>
                <Typography level="title-sm">Active token owners</Typography>
                <Table aria-label="DAT token owners" stickyHeader>
                    <thead>
                        <tr>
                            <th>Project</th>
                            <th>Email</th>
                            <th>Source</th>
                            <th>Expires</th>
                        </tr>
                    </thead>
                    <tbody>
                        {owners.map((owner) => (
                            <tr key={owner.userId}>
                                <td>{owner.projectName || owner.projectNames.join(', ') || 'Unknown'}</td>
                                <td>{owner.email || 'Unknown'}</td>
                                <td>{owner.source}</td>
                                <td>{owner.expiresAt ? new Date(owner.expiresAt).toLocaleString() : 'Unknown'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {loading && <Typography level="body-sm">Loading…</Typography>}
            </Card>
        </Sheet>
    )
}
